import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSubscription } from "../../../../hooks/useSubscription";

interface CategoryScores {
  marketOpportunity: number;
  customerUnderstanding: number;
  valueAndProduct: number;
  businessModel: number;
  validationExecution: number;
}

interface Props {
  projectProgress: number;
  stepsStarted: number;
  className?: string;
  categoryScores?: CategoryScores;
  onToolOpen?: (toolId: string) => void;
}

interface PhaseTool {
  toolId: string;
  icon: string;
  label: string;
}

interface Phase {
  id: string;
  label: string;
  subtitle: string;
  icon: string;
  steps: string;
  requirement: (stepsStarted: number, projectProgress: number) => boolean;
  scoreKey?: keyof CategoryScores;
  tools?: PhaseTool[];
}

const PHASES: Phase[] = [
  {
    id: "idea",
    label: "Idea",
    subtitle: "Create project",
    icon: "bi-lightbulb",
    steps: "Start",
    requirement: () => true,
    tools: [{ toolId: "branding", icon: "bi-palette", label: "Branding" }],
  },
  {
    id: "market",
    label: "Market",
    subtitle: "Steps 1-5",
    icon: "bi-graph-up-arrow",
    steps: "Market Opportunity",
    requirement: (s) => s >= 5,
    scoreKey: "marketOpportunity",
  },
  {
    id: "customer",
    label: "Customer",
    subtitle: "Steps 6-10",
    icon: "bi-people",
    steps: "Customer Understanding",
    requirement: (s) => s >= 10,
    scoreKey: "customerUnderstanding",
    tools: [{ toolId: "survey", icon: "bi-clipboard2-data", label: "Survey" }],
  },
  {
    id: "product",
    label: "Product",
    subtitle: "Steps 11-14",
    icon: "bi-box-seam",
    steps: "Value & Product",
    requirement: (s) => s >= 14,
    scoreKey: "valueAndProduct",
    tools: [
      { toolId: "logo", icon: "bi-image", label: "Logo" },
      { toolId: "website", icon: "bi-globe", label: "Website" },
    ],
  },
  {
    id: "model",
    label: "Business Model",
    subtitle: "Steps 15-19",
    icon: "bi-currency-dollar",
    steps: "Business Model",
    requirement: (s) => s >= 19,
    scoreKey: "businessModel",
  },
  {
    id: "validation",
    label: "Validated",
    subtitle: "Steps 20-24",
    icon: "bi-check2-circle",
    steps: "Validation & Execution",
    requirement: (s) => s >= 24,
    scoreKey: "validationExecution",
  },
  {
    id: "mvp",
    label: "Build MVP",
    subtitle: "Generate specs",
    icon: "bi-code-slash",
    steps: "MVP & Prototype",
    requirement: (_s, p) => p >= 100,
    tools: [{ toolId: "mvp", icon: "bi-magic", label: "MVP" }],
  },
  {
    id: "marketing",
    label: "Marketing",
    subtitle: "Plan & assets",
    icon: "bi-megaphone",
    steps: "Marketing & Brand",
    requirement: (_s, p) => p >= 100,
    tools: [
      { toolId: "marketing", icon: "bi-megaphone", label: "Marketing" },
      { toolId: "persona", icon: "bi-person-check", label: "Persona Test" },
      { toolId: "seo", icon: "bi-search", label: "SEO" },
      { toolId: "content", icon: "bi-file-text", label: "Content" },
      { toolId: "email", icon: "bi-envelope-at", label: "Email" },
      { toolId: "social", icon: "bi-share", label: "Social" },
    ],
  },
  {
    id: "launch",
    label: "Launch",
    subtitle: "Go to market",
    icon: "bi-rocket-takeoff",
    steps: "Launch",
    requirement: (_s, p) => p >= 100,
  },
];

const getQualityColor = (score: number): string => {
  if (score >= 60) return "#50cd89";
  if (score >= 40) return "#ffc700";
  return "#f1416c";
};

const getQualityLabel = (score: number): string => {
  if (score >= 80) return "Excellent";
  if (score >= 60) return "Strong";
  if (score >= 40) return "Fair";
  return "Weak";
};

export const MilestoneBanner: React.FC<Props> = ({
  projectProgress,
  stepsStarted,
  className = "",
  categoryScores,
  onToolOpen,
}) => {
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const { canUseFeature } = useSubscription();
  const phaseStatuses = PHASES.map((phase, index) => {
    const isComplete = phase.requirement(stepsStarted, projectProgress);
    const prevComplete =
      index === 0 || PHASES[index - 1]!.requirement(stepsStarted, projectProgress);
    const isCurrent = !isComplete && prevComplete;
    const score = phase.scoreKey && categoryScores ? categoryScores[phase.scoreKey] : undefined;
    return { ...phase, isComplete, isCurrent, score };
  });

  const completedCount = phaseStatuses.filter((p) => p.isComplete).length;
  const overallPercent = Math.round((completedCount / PHASES.length) * 100);

  return (
    <div className={`card ${className}`}>
      <div className="card-body py-6 px-7">
        {/* Header */}
        <div className="mb-7 text-center">
          <h4 className="fw-bolder text-gray-800 mb-0 fs-4">Your Journey to Launch</h4>
        </div>

        {/* Phase Map */}
        <div className="d-flex align-items-start position-relative">
          {phaseStatuses.map((phase, index) => {
            const isLast = index === PHASES.length - 1;
            const hasScore = phase.isComplete && phase.score !== undefined;
            const qualityColor = hasScore ? getQualityColor(phase.score!) : undefined;

            return (
              <div
                key={phase.id}
                className="d-flex flex-column align-items-center position-relative"
                style={{ flex: 1, minWidth: 0 }}
              >
                {/* Connector line */}
                {!isLast && (
                  <div
                    className="position-absolute"
                    style={{
                      top: "18px",
                      left: "50%",
                      width: "100%",
                      height: "3px",
                      zIndex: 0,
                    }}
                  >
                    <div
                      className="h-100"
                      style={{
                        borderRadius: "2px",
                        background: phase.isComplete ? "var(--bs-primary)" : "var(--bs-gray-300)",
                        opacity: phase.isComplete ? 0.4 : 1,
                      }}
                    ></div>
                  </div>
                )}

                {/* Node */}
                <div
                  className={`d-flex align-items-center justify-content-center rounded-circle position-relative ${
                    phase.isComplete
                      ? "bg-primary shadow-sm"
                      : phase.isCurrent
                      ? "bg-white border border-2 border-primary shadow"
                      : "bg-white border border-1 border-gray-300"
                  }`}
                  style={{
                    width: "36px",
                    height: "36px",
                    minWidth: "36px",
                    zIndex: 1,
                    transition: "all 0.3s ease",
                  }}
                >
                  {phase.isComplete ? (
                    <i className="bi bi-check-lg text-white fs-5"></i>
                  ) : (
                    <i
                      className={`bi ${phase.icon} fs-7 ${
                        phase.isCurrent ? "text-primary" : "text-gray-400"
                      }`}
                    ></i>
                  )}

                  {/* Quality ring for completed phases with scores */}
                  {hasScore && (
                    <div
                      className="position-absolute rounded-circle"
                      style={{
                        width: "44px",
                        height: "44px",
                        border: `3px solid ${qualityColor}`,
                        opacity: 0.8,
                      }}
                    ></div>
                  )}

                  {/* Pulse ring for current phase */}
                  {phase.isCurrent && (
                    <div
                      className="position-absolute rounded-circle border border-2 border-primary"
                      style={{
                        width: "46px",
                        height: "46px",
                        opacity: 0.3,
                        animation: "pulse-ring 2s ease-in-out infinite",
                      }}
                    ></div>
                  )}
                </div>

                {/* Label */}
                <span
                  className={`mt-2 text-center fw-semibold ${
                    phase.isComplete
                      ? "text-gray-800"
                      : phase.isCurrent
                      ? "text-primary fw-bold"
                      : "text-gray-400"
                  }`}
                  style={{ fontSize: "11px", lineHeight: "1.2" }}
                >
                  {phase.label}
                </span>

                {/* Score badge or subtitle */}
                {hasScore ? (
                  <span
                    className="text-center fw-bold"
                    style={{
                      fontSize: "9px",
                      lineHeight: "1.2",
                      color: qualityColor,
                    }}
                  >
                    {phase.score}/100 · {getQualityLabel(phase.score!)}
                  </span>
                ) : (
                  <span
                    className={`text-center ${
                      phase.isCurrent ? "text-gray-600" : "text-gray-400"
                    }`}
                    style={{ fontSize: "9px", lineHeight: "1.2" }}
                  >
                    {phase.subtitle}
                  </span>
                )}

                {/* Tool icons */}
                {phase.tools && phase.tools.length > 0 && onToolOpen && (
                  <div className="d-flex gap-1 mt-2">
                    {phase.tools.map((tool) => {
                      const isGenTool = ["logo", "marketing", "website", "survey", "mvp", "persona"].includes(tool.toolId);
                      const tierLocked = isGenTool && !canUseFeature("generation_tools");
                      const isUnlocked = !tierLocked;

                      return (
                        <div
                          key={tool.toolId}
                          className="position-relative"
                          onMouseEnter={() => !isUnlocked ? setShowTooltip(tool.toolId) : undefined}
                          onMouseLeave={() => setShowTooltip(null)}
                        >
                          <button
                            className={`btn btn-icon btn-sm milestone-tool-btn ${isUnlocked ? "btn-light-primary" : "btn-light"}`}
                            onClick={() => isUnlocked && onToolOpen(tool.toolId)}
                            disabled={!isUnlocked}
                            title={tool.label}
                            style={{ width: "32px", height: "32px", minWidth: "32px" }}
                          >
                            {!isUnlocked && (
                              <i className="bi bi-lock text-gray-600" style={{ fontSize: "10px", position: "absolute", top: "1px", right: "1px" }}></i>
                            )}
                            <i className={`bi ${tool.icon} milestone-tool-icon ${isUnlocked ? "text-primary" : "text-gray-500"}`} style={{ fontSize: "14px" }}></i>
                          </button>

                          {/* Paywall tooltip */}
                          {showTooltip === tool.toolId && !isUnlocked && (
                            <div
                              className="position-absolute bg-dark text-white rounded p-3 shadow-lg"
                              style={{
                                top: "100%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                marginTop: "8px",
                                zIndex: 1000,
                                minWidth: "200px",
                                fontSize: "12px",
                              }}
                            >
                              <div className="fw-bold mb-1">{tool.label}</div>
                              <div className="d-flex align-items-center mb-2">
                                <i className="bi bi-stars text-warning me-2"></i>
                                <span>Available on the <strong>Business</strong> plan</span>
                              </div>
                              <Link
                                to="/profile/subscription"
                                className="btn btn-sm btn-warning w-100 text-dark fw-bold"
                                onClick={() => setShowTooltip(null)}
                              >
                                Upgrade to unlock
                              </Link>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pulse animation and tool button hover */}
      <style>{`
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.15); opacity: 0.1; }
          100% { transform: scale(1); opacity: 0.3; }
        }
        .milestone-tool-btn:hover .milestone-tool-icon,
        .milestone-tool-btn:focus .milestone-tool-icon {
          color: white !important;
        }
      `}</style>
    </div>
  );
};
